import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography
} from "@mui/material";
import { TableColumnsPicker } from "components/Documents/Report-sections-forms/TableColumnsPicker";
import { SelectAutocomplete } from "components/forms";
import { calcFieldsToDisplay, DEAL_INDEX, indexNames } from "constants/reports";
import { usePrompt } from "hooks";
import { difference, startCase } from "lodash";
import {
  useAccountSelector,
  useDealActions,
  useMeasuresSelector,
  useReportsActions,
  useReportsMetaSelector,
  useReportsQueriesSelector
} from "store";
import { CanvasView, ChartType, CustomConfig } from "store/types";
import { normalizeGroupByCharacteristic } from "utils";

import { TableGroupByResult, TableGroupBySection } from "./TableGroupBySection";

interface CustomizationModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (config: CustomConfig) => void;
  view: CanvasView | null;
}

const CHART_TYPES: Array<{ value: ChartType; label: string }> = [
  { value: "pie", label: "Pie" },
  { value: "bar", label: "Bar" },
  { value: "column", label: "Column" },
  { value: "barStacked", label: "Stacked Bar" },
  { value: "columnStacked", label: "Stacked Column" },
  { value: "waterfall", label: "Waterfall" },
  { value: "top5", label: "Top 5" },
  { value: "scatter", label: "X/Y Scatter" },
  { value: "clusterBar", label: "Cluster Bar" }
];

const StyledChip = styled(Chip)(() => ({
  color: "#071F35",
  fontSize: "12px",
  padding: "4px 8px",
  height: "28px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#e2e8f0"
  }
}));

const Title = styled(Typography)(() => ({
  fontSize: "14px",
  lineHeight: "20px",
  fontWeight: 500,
  color: "#1e293b"
}));

export const CustomizationModal: React.FC<CustomizationModalProps> = ({ open, onClose, onApply, view }) => {
  const { getMeasuresAction } = useDealActions();
  const { getPieChartAction, getCubicAction, getReportTableAction, askAiAction, getMeasureSummaryAction } =
    useReportsActions();
  const measures = useMeasuresSelector();
  const { support_investor_groups } = useAccountSelector() || {};
  const { KPIFields, KPICharacteristics, characteristics } = useReportsMetaSelector();
  const { filters, dateRange, currency, exchangeMethod, use_accounting_date } = useReportsQueriesSelector();
  const openPrompt = usePrompt();

  // KPI options for autocomplete (same as InfoCard)
  const kpiOptionsForSelect = useMemo(() => {
    return [...KPIFields, ...KPICharacteristics];
  }, [KPIFields, KPICharacteristics]);

  const groupByOptions = useMemo(
    () => [
      { text: "Investor", key: "investor", value: "investor" },
      ...(support_investor_groups ? [{ text: "Investor Group", key: "investorGroup", value: "investorGroup" }] : []),
      { text: "Deal", key: "deal", value: "deal" },
      ...characteristics.map((char) =>
        typeof char === "string" ? { text: char, key: char, value: normalizeGroupByCharacteristic(char) } : char
      )
    ],
    [characteristics, support_investor_groups]
  );

  const [loadingMeasures, setLoadingMeasures] = useState(false);
  const [applyingConfig, setApplyingConfig] = useState(false);

  const [chartType, setChartType] = useState<ChartType>("pie");
  const [valueColumn, setValueColumn] = useState<string>("");
  const [groupByColumn, setGroupByColumn] = useState<string>("");
  const [textContent, setTextContent] = useState<string>("");
  const [aiQuestion, setAiQuestion] = useState<string>("");
  const [kpiMetrics, setKpiMetrics] = useState<Array<{ title: string; filter: string }>>([]);

  // Initialize default KPI metrics from kpiOptionsForSelect
  const getDefaultKpiMetrics = useCallback(() => {
    return kpiOptionsForSelect.slice(0, 3).map((opt) => ({ title: opt, filter: opt }));
  }, [kpiOptionsForSelect]);
  const [summaryMeasure, setSummaryMeasure] = useState<string>("");

  // Table card state
  const [tableColumns, setTableColumns] = useState<{
    measures: string[];
    characteristics: string[];
    calcFields: string[];
  }>({ measures: [], characteristics: [], calcFields: [] });
  const [tableGroupBy, setTableGroupBy] = useState<string[]>(["none", "none"]);
  const [rowBy, setRowBy] = useState<"deal" | "investor" | "measure">("deal");
  const [indexName, setIndexName] = useState<string | undefined>(undefined);
  const [pmeMenuOpen, setPmeMenuOpen] = useState(false);
  const pmeChipRef = useRef<HTMLDivElement>(null);

  // Combined table parameters for display
  const tableParameters = useMemo(() => {
    return [
      ...tableColumns.measures,
      ...tableColumns.characteristics,
      ...difference(tableColumns.calcFields, calcFieldsToDisplay)
    ];
  }, [tableColumns]);

  // Parameters selected outside of calcFieldsToDisplay (from TableColumnsPicker)
  const selectedUniqueParameters = useMemo(() => {
    return [
      ...difference(tableColumns.calcFields, calcFieldsToDisplay),
      ...tableColumns.characteristics,
      ...tableColumns.measures
    ];
  }, [tableColumns]);

  // Fetch measures when modal opens
  useEffect(() => {
    if (open && measures.length === 0) {
      setLoadingMeasures(true);
      getMeasuresAction().finally(() => setLoadingMeasures(false));
    }
  }, [open, measures.length, getMeasuresAction]);

  // Reset state when view changes
  useEffect(() => {
    if (view?.customConfig) {
      const config = view.customConfig;

      // Chart: restore from graphDataFormation
      if (config.graphDataFormation) {
        const chartKey = Object.keys(config.graphDataFormation)[0];
        const chartConfig = config.graphDataFormation[chartKey];
        setChartType(chartConfig?.chartType || "pie");
        setValueColumn(chartConfig?.measure || "");
        setGroupByColumn(chartConfig?.groupBy || "");
      } else {
        setChartType("pie");
        setValueColumn("");
        setGroupByColumn("");
      }

      // Text card
      setTextContent(config.content || "");

      // AI card
      setAiQuestion(config.question || "");

      // KPI: restore from cubics
      if (config.cubics) {
        const metrics = Object.values(config.cubics).map((c) => ({
          title: c.title,
          filter: c.filter
        }));
        setKpiMetrics(metrics.length > 0 ? metrics : getDefaultKpiMetrics());
      } else {
        setKpiMetrics(getDefaultKpiMetrics());
      }

      // Summary: restore from parameter
      setSummaryMeasure(config.parameter || "");

      // Data table: restore from config
      setTableColumns({
        measures: config.measures || [],
        characteristics: config.characteristics || [],
        calcFields: config.calculatedFields || []
      });
      const restoredGroupBy = config.rawGroupBy || [config.groupBy?.[0]?.[0] || "none", "none"];
      setTableGroupBy(restoredGroupBy);
      setRowBy(config.rowBy || "deal");
      setIndexName(config.indexName);
    } else {
      setChartType("pie");
      setValueColumn("");
      setGroupByColumn("");
      setTextContent("");
      setAiQuestion("");
      setKpiMetrics(getDefaultKpiMetrics());
      setSummaryMeasure("");
      setTableColumns({ measures: [], characteristics: [], calcFields: [] });
      setTableGroupBy(["none", "none"]);
      setRowBy("deal");
      setIndexName(undefined);
    }
  }, [view, getDefaultKpiMetrics]);

  const handleApply = useCallback(async () => {
    const config: CustomConfig = {};

    if (view?.type.includes("chart")) {
      // Fetch chart data from API
      if (valueColumn && groupByColumn) {
        setApplyingConfig(true);
        try {
          const chartKey = "chart1";
          const graphDataFormation = {
            [chartKey]: {
              measure: valueColumn,
              groupBy: groupByColumn,
              chartType: chartType as ChartType
            }
          };

          // Save API payload fields directly in config
          config.graphDataFormation = graphDataFormation;

          const chartConfig = graphDataFormation[chartKey];
          const isCalcField = chartConfig.measure.startsWith("calc-field-");

          const chartData = await getPieChartAction({
            filters,
            dateRange,
            currency,
            exchangeDate: exchangeMethod,
            measures: isCalcField ? [] : [chartConfig.measure],
            calculatedFields: isCalcField ? [chartConfig.measure.split("calc-field-")[1]] : [],
            groupBy: [[normalizeGroupByCharacteristic(chartConfig.groupBy)]],
            chartName: chartKey,
            chartType,
            processKey: `customization-${chartKey}`,
            pageType: "document"
          });

          if (chartData) {
            config.chartData = chartData;
          }
        } catch (error) {
          console.error("Failed to fetch chart data:", error);
        } finally {
          setApplyingConfig(false);
        }
      }
    } else if (view?.type === "text_card") {
      config.content = textContent;
    } else if (view?.type === "ai_card") {
      config.question = aiQuestion;

      // Fetch AI response
      if (aiQuestion.trim()) {
        setApplyingConfig(true);
        try {
          const aiResponse = await askAiAction({ question: aiQuestion });
          if (aiResponse) {
            config.answer = aiResponse.answer;
            config.chartData = { answer: aiResponse.answer };
          }
        } catch (error) {
          console.error("Failed to fetch AI response:", error);
        } finally {
          setApplyingConfig(false);
        }
      }
    } else if (view?.type === "kpi_card") {
      // Fetch KPI data from API
      if (kpiMetrics.length > 0) {
        setApplyingConfig(true);
        try {
          const cubics: Record<string, { title: string; filter: string }> = {};
          kpiMetrics.forEach((metric, index) => {
            cubics[`cubic${index + 1}`] = {
              title: metric.title,
              filter: metric.filter
            };
          });

          // Save API payload field directly in config
          config.cubics = cubics;

          const kpiData = await getCubicAction({
            cubics,
            pageType: "document",
            filters,
            dateRange,
            currency,
            exchangeMethod,
            use_accounting_date
          });

          if (kpiData) {
            config.chartData = kpiData;
          }
        } catch (error) {
          console.error("Failed to fetch KPI data:", error);
        } finally {
          setApplyingConfig(false);
        }
      }
    } else if (view?.type === "summary_card") {
      // Fetch summary data using measure-summary API
      if (summaryMeasure) {
        // Save API payload field directly in config
        config.parameter = summaryMeasure;

        setApplyingConfig(true);
        try {
          const summaryData = await getMeasureSummaryAction({
            parameter: summaryMeasure,
            filters,
            dateRange,
            currency,
            exchangeMethod,
            use_accounting_date
          });

          if (summaryData) {
            config.chartData = {
              parameter: summaryData.parameter,
              total: summaryData.total,
              average: summaryData.average,
              min: summaryData.min,
              max: summaryData.max,
              measureName: summaryMeasure
            };
          }
        } catch (error) {
          console.error("Failed to fetch summary data:", error);
        } finally {
          setApplyingConfig(false);
        }
      }
    } else if (view?.type === "data_table") {
      const {
        measures: selectedMeasures,
        calcFields: selectedCalcFields,
        characteristics: selectedCharacteristics
      } = tableColumns;
      const allColumns = [...selectedMeasures, ...selectedCalcFields, ...selectedCharacteristics];
      const columnsForApi = allColumns.map((col) => ({ aggregation: col }));
      const allGroupBy = [...tableGroupBy.filter((g) => g && g !== "none"), rowBy];

      // Save API payload fields directly in config
      config.measures = selectedMeasures;
      config.calculatedFields = selectedCalcFields;
      config.characteristics = selectedCharacteristics;
      config.columns = columnsForApi;
      config.groupBy = allGroupBy.map((g) => [g]);
      config.rawGroupBy = [...tableGroupBy];
      config.columnBy = "measure";
      config.rowBy = rowBy;
      config.indexName = indexName;

      // Fetch table data from API
      if (allColumns.length) {
        setApplyingConfig(true);
        try {
          const tableData = await getReportTableAction({
            dateRange,
            currency,
            exchangeMethod,
            filters,
            measures: selectedMeasures,
            calculatedFields: selectedCalcFields,
            characteristics: selectedCharacteristics,
            columns: columnsForApi,
            groupBy: allGroupBy.map((g) => [g]),
            columnBy: "measure",
            rowBy: rowBy,
            originalCurrency: false,
            use_accounting_date,
            disableDispatch: true,
            indexName
          });

          if (tableData && tableData.tableData) {
            const apiColumns = tableData.tableData.columns || [];
            config.chartData = {
              columns: [
                { key: "name", title: "Name" },
                ...apiColumns.map((col) => ({ key: col.key, title: col.title }))
              ],
              data: (tableData.tableData.rows || []) as Array<{
                title?: string;
                valueData?: Record<string, number | null>;
              }>
            };
          }
        } catch (error) {
          console.error("Failed to fetch table data:", error);
        } finally {
          setApplyingConfig(false);
        }
      }
    }

    onApply(config);
    onClose();
  }, [
    view,
    chartType,
    valueColumn,
    groupByColumn,
    textContent,
    aiQuestion,
    kpiMetrics,
    summaryMeasure,
    tableParameters,
    tableColumns,
    tableGroupBy,
    indexName,
    onApply,
    onClose,
    getPieChartAction,
    getCubicAction,
    getReportTableAction,
    askAiAction,
    getMeasureSummaryAction,
    filters,
    dateRange,
    currency,
    exchangeMethod,
    use_accounting_date
  ]);

  const getTitle = () => {
    if (!view) return "Customize";
    switch (view.type) {
      case "chart":
        return "Customize Graphs / Charts";
      case "data_table":
        return "Customize Data Table";
      case "summary_card":
        return "Customize Summary Card";
      case "kpi_card":
        return "Customize DPI / IRR / NAV";
      case "text_card":
        return "Customize Text Card";
      case "ai_card":
        return "Ask AI";
      default:
        return "Customize";
    }
  };

  // Prepare options for Autocomplete components
  const measureOptions = useMemo(
    () => measures.map((measure) => ({ value: measure.name, label: measure.name })),
    [measures]
  );

  const renderChartOptions = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <FormControl fullWidth>
        <InputLabel>Chart Type</InputLabel>
        <Select value={chartType} label="Chart Type" onChange={(e) => setChartType(e.target.value as ChartType)}>
          {CHART_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Autocomplete
        fullWidth
        options={measureOptions}
        value={measureOptions.find((opt) => opt.value === valueColumn) || null}
        onChange={(_, newValue) => setValueColumn(newValue?.value || "")}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        loading={loadingMeasures}
        disabled={loadingMeasures}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Value"
            placeholder="Select"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingMeasures ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }
            }}
          />
        )}
      />
      <Autocomplete
        fullWidth
        options={groupByOptions}
        value={groupByOptions.find((opt) => opt.value === groupByColumn) || null}
        onChange={(_, newValue) => setGroupByColumn(newValue?.value || "")}
        getOptionLabel={(option) => option.text}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => <TextField {...params} label="Group By" placeholder="Select" />}
      />
    </Box>
  );

  const renderTextOptions = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, color: "#1e293b" }}>
        Text Content
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={8}
        value={textContent}
        onChange={(e) => setTextContent(e.target.value)}
        placeholder="Enter your text content here..."
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px"
          }
        }}
      />
      <Typography variant="caption" sx={{ color: "#64748b" }}>
        Enter plain text content for your card.
      </Typography>
    </Box>
  );

  const renderAIOptions = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, color: "#1e293b" }}>
        Ask AI a Question
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={4}
        label="Your Question"
        value={aiQuestion}
        onChange={(e) => setAiQuestion(e.target.value)}
        placeholder="e.g., What is the total NAV across all investments?"
      />
      <Typography variant="caption" sx={{ color: "#64748b" }}>
        Ask any question about your portfolio data and AI will provide an answer.
      </Typography>
    </Box>
  );

  const handleAddMetric = () => {
    if (kpiMetrics.length < 5) {
      setKpiMetrics([...kpiMetrics, { title: "", filter: "" }]);
    }
  };

  const handleRemoveMetric = (index: number) => {
    setKpiMetrics(kpiMetrics.filter((_, i) => i !== index));
  };

  const renderKPIOptions = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, color: "#1e293b" }}>
        KPI Metrics (up to 5)
      </Typography>
      {kpiMetrics.map((metric, index) => (
        <Box key={index} sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <TextField
            label="Title"
            value={metric.title}
            onChange={(e) => {
              const updated = [...kpiMetrics];
              updated[index] = { ...updated[index], title: e.target.value };
              setKpiMetrics(updated);
            }}
            sx={{
              flex: 1
            }}
          />
          <SelectAutocomplete<string>
            options={kpiOptionsForSelect}
            defaultValue={kpiOptionsForSelect.includes(metric.filter) ? metric.filter : null}
            onChange={(value) => {
              if (!value) return;
              const updated = [...kpiMetrics];
              updated[index] = {
                ...updated[index],
                filter: value as string,
                // Auto-update title if it was the same as old filter or empty
                title:
                  updated[index].title === updated[index].filter || !updated[index].title
                    ? (value as string)
                    : updated[index].title
              };
              setKpiMetrics(updated);
            }}
            disableClearable
            renderInput={(params) => <TextField {...params} label="Value" size="small" />}
            sx={{ flex: 1 }}
          />
          <IconButton
            onClick={() => handleRemoveMetric(index)}
            sx={{ color: "#071F35", p: 0.5 }}
            disabled={kpiMetrics.length === 1}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      ))}
      <Button
        disabled={kpiMetrics.length >= 5}
        variant="outlined"
        onClick={handleAddMetric}
        startIcon={<AddIcon />}
        sx={{
          maxWidth: "140px"
        }}
      >
        Add Metric
      </Button>
      <Typography variant="caption" sx={{ color: "#64748b", mt: 1 }}>
        Add up to 5 KPIs (e.g., DPI, IRR, NAV, TVPI, MOIC).
      </Typography>
    </Box>
  );

  // Toggle parameter selection (for calcFieldsToDisplay chips)
  const handleToggleParameter = (param: string) => {
    if (param === "PME") {
      setPmeMenuOpen(true);
      return;
    }
    setTableColumns((prev) => ({
      ...prev,
      calcFields: prev.calcFields.includes(param)
        ? prev.calcFields.filter((p) => p !== param)
        : [...prev.calcFields, param]
    }));
  };

  const handleSelectIndex = (selectedIndex: string) => {
    setPmeMenuOpen(false);
    if (selectedIndex === "none") {
      setTableColumns((prev) => ({
        ...prev,
        calcFields: prev.calcFields.filter((p) => p !== "PME")
      }));
      setIndexName(undefined);
    } else if (selectedIndex === DEAL_INDEX) {
      setTableColumns((prev) => ({
        ...prev,
        calcFields: prev.calcFields.includes("PME") ? prev.calcFields : [...prev.calcFields, "PME"]
      }));
      setIndexName(undefined);
    } else {
      setTableColumns((prev) => ({
        ...prev,
        calcFields: prev.calcFields.includes("PME") ? prev.calcFields : [...prev.calcFields, "PME"]
      }));
      setIndexName(selectedIndex);
    }
  };

  // Open TableColumnsPicker for advanced parameter selection
  const handleOpenColumnsPicker = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      const newColumnsSet = await openPrompt({
        Component: TableColumnsPicker,
        componentType: "popover",
        anchorEl: event.currentTarget,
        props: {
          selectedColumns: {
            ...tableColumns,
            calcFields: difference(tableColumns.calcFields, calcFieldsToDisplay)
          }
        }
      });

      if (!newColumnsSet) return;

      setTableColumns({
        characteristics: newColumnsSet.characteristics,
        measures: newColumnsSet.measures,
        calcFields: [
          ...calcFieldsToDisplay.filter((field) => tableColumns.calcFields.includes(field)),
          ...newColumnsSet.calcFields
        ]
      });
    },
    [openPrompt, tableColumns]
  );

  // Open TableGroupBySection popover for group by selection
  const handleOpenGroupBy = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      const result = await openPrompt({
        Component: TableGroupBySection,
        componentType: "popover",
        transformOrigin: {
          vertical: "top",
          horizontal: "left"
        },
        anchorEl: event.currentTarget,
        props: {
          groupBy: tableGroupBy as [string, string],
          rowBy: rowBy
        }
      });

      if (!result) return;

      const { groupBy: newGroupBy, rowBy: newRowBy } = result as TableGroupByResult;
      setTableGroupBy(newGroupBy);
      setRowBy(newRowBy);
    },
    [openPrompt, tableGroupBy, rowBy]
  );

  // Get group by chips: 2 groupBy levels + rowBy as last group
  const groupByChips = useMemo(() => {
    const groups =
      tableGroupBy.length < 2
        ? [...tableGroupBy, ...Array(2 - tableGroupBy.length).fill("none")]
        : tableGroupBy.slice(0, 2);
    return [...groups, rowBy];
  }, [tableGroupBy, rowBy]);

  const renderTableOptions = () => (
    <Stack sx={{ gap: "16px" }}>
      {/* Parameters Section */}
      <Stack sx={{ gap: "8px" }}>
        <Title>Parameters</Title>
        <Stack sx={{ flexDirection: "row", gap: "10px", flexWrap: "wrap" }}>
          {calcFieldsToDisplay.map((parameter) => (
            <StyledChip
              key={parameter}
              ref={parameter === "PME" ? pmeChipRef : undefined}
              onClick={() => handleToggleParameter(parameter)}
              label={parameter}
              sx={{
                ...(tableColumns.calcFields.includes(parameter) && {
                  backgroundColor: "#c3c9cf !important"
                })
              }}
            />
          ))}
          <Menu anchorEl={pmeChipRef.current} open={pmeMenuOpen} onClose={() => setPmeMenuOpen(false)}>
            <MenuItem onClick={() => handleSelectIndex("none")}>None</MenuItem>
            <MenuItem
              selected={!indexName && tableColumns.calcFields.includes("PME")}
              onClick={() => handleSelectIndex(DEAL_INDEX)}
            >
              Use deal specific index
            </MenuItem>
            {Object.entries(indexNames).map(([index, name]) => (
              <MenuItem key={index} selected={index === indexName} onClick={() => handleSelectIndex(index)}>
                {name}
              </MenuItem>
            ))}
          </Menu>
          {selectedUniqueParameters.length > 0 && (
            <StyledChip
              label={`(${selectedUniqueParameters.length})`}
              sx={{
                cursor: "default",
                backgroundColor: "#c3c9cf !important"
              }}
            />
          )}
          {loadingMeasures ? (
            <CircularProgress size="20px" />
          ) : (
            <Button
              variant="text"
              size="sm"
              sx={{ color: "#26A6AE", fontSize: "14px", height: "28px" }}
              onClick={handleOpenColumnsPicker}
            >
              More
            </Button>
          )}
        </Stack>
      </Stack>
      {/* Group By Section */}
      <Stack sx={{ gap: "8px" }}>
        <Title>Group by</Title>
        <Stack sx={{ flexDirection: "row", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
          {groupByChips.map((item, index) => (
            <Stack key={index} sx={{ flexDirection: "row", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
              <StyledChip onClick={handleOpenGroupBy} label={item === "none" ? "None" : startCase(item)} />
              {index < groupByChips.length - 1 && <ArrowForwardIosIcon sx={{ fontSize: "14px", color: "#64748b" }} />}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );

  const renderSummaryOptions = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, color: "#1e293b" }}>
        Select a measure to display summary statistics (Total, Average, Min, Max)
      </Typography>
      <Autocomplete
        fullWidth
        options={measureOptions}
        value={measureOptions.find((opt) => opt.value === summaryMeasure) || null}
        onChange={(_, newValue) => setSummaryMeasure(newValue?.value || "")}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        loading={loadingMeasures}
        disabled={loadingMeasures}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Measure"
            placeholder="Search measures..."
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingMeasures ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }
            }}
          />
        )}
      />
      <Typography variant="caption" sx={{ color: "#64748b", mt: 1 }}>
        The summary card will display Total, Average, Min, and Max values for the selected measure.
      </Typography>
    </Box>
  );

  const renderContent = () => {
    if (!view) return null;

    switch (view.type) {
      case "chart":
        return renderChartOptions();
      case "data_table":
        return renderTableOptions();
      case "summary_card":
        return renderSummaryOptions();
      case "kpi_card":
        return renderKPIOptions();
      case "text_card":
        return renderTextOptions();
      case "ai_card":
        return renderAIOptions();
      default:
        return (
          <Typography variant="body2" color="textSecondary">
            No customization options available for this view type.
          </Typography>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { p: "16px" } } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "10px 0px" }}>
        {getTitle()}
        <IconButton onClick={onClose} size="sm">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: "0px" }}>
        <Box sx={{ py: "10px" }}>{renderContent()}</Box>
      </DialogContent>
      <DialogActions sx={{ py: "10px" }}>
        <Button onClick={onClose} color="inherit" disabled={applyingConfig}>
          Cancel
        </Button>
        <Button
          {...(applyingConfig && { startIcon: <CircularProgress size={20} sx={{ mr: 1 }} /> })}
          onClick={handleApply}
          variant="contained"
          disabled={applyingConfig}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
